import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHelpers";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { PawPrint, Plus } from "lucide-react";

export default function PetRegistryPage() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ pet_name: "", species: "Dog", breed: "", weight_lbs: 0 });
  const [open, setOpen] = useState(false);
  const load = async () => { const { data } = await api.get("/pets"); setItems(data.items); };
  useEffect(() => { load(); }, []);
  const submit = async () => {
    if (!form.pet_name) return toast.error("Pet name required");
    await api.post("/pets", { ...form, weight_lbs: Number(form.weight_lbs) });
    toast.success("Submitted for approval");
    setForm({ pet_name: "", species: "Dog", breed: "", weight_lbs: 0 });
    setOpen(false);
    load();
  };
  return (
    <div>
      <PageHeader
        eyebrow="Community"
        title="Pet Registry"
        subtitle="Register your pets — required for building access and amenity use."
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-pet" className="rounded-full bg-zinc-900 hover:bg-zinc-800"><Plus className="h-4 w-4 mr-1" />Register pet</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Register pet</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Pet name</Label><Input data-testid="pet-name" value={form.pet_name} onChange={(e) => setForm({ ...form, pet_name: e.target.value })} /></div>
                <div>
                  <Label>Species</Label>
                  <Select value={form.species} onValueChange={(v) => setForm({ ...form, species: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{["Dog","Cat","Bird","Fish","Reptile","Other"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Breed</Label><Input value={form.breed} onChange={(e) => setForm({ ...form, breed: e.target.value })} /></div>
                <div><Label>Weight (lbs)</Label><Input type="number" value={form.weight_lbs} onChange={(e) => setForm({ ...form, weight_lbs: e.target.value })} /></div>
                <Button onClick={submit} className="w-full">Submit</Button>
              </div>
            </DialogContent>
          </Dialog>
        }
      />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((p) => (
          <Card key={p.id} className="p-5 bg-white">
            <div className="flex items-start gap-3">
              <div className="h-12 w-12 rounded-md bg-zinc-100 border border-black/5 flex items-center justify-center">
                <PawPrint className="h-6 w-6 text-zinc-700" />
              </div>
              <div className="flex-1">
                <div className="text-lg font-semibold font-display">{p.pet_name}</div>
                <div className="text-xs text-zinc-500">{p.species} · {p.breed || "—"} · {p.weight_lbs} lbs</div>
                <Badge variant="outline" className="capitalize mt-2">{p.status}</Badge>
              </div>
            </div>
          </Card>
        ))}
        {items.length === 0 && <div className="text-sm text-muted-foreground">No pets registered.</div>}
      </div>
    </div>
  );
}
